'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Icon, IconName } from '@/components/atoms/Icon';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { ContactForm } from '@/components/organisms/shared/contact/ContactForm';
import styles from './ContactSupportContent.module.scss';

interface QuickSolution {
  id: string;
  icon: string;
  title: string;
  description: string;
  action: string;
  link?: string;
}

interface ContactSupportContentProps {
  className?: string;
  supportType?: 'technical' | 'billing' | 'general';
  title?: string;
  subtitle?: string;
}

const quickSolutions: QuickSolution[] = [
  {
    id: 'api',
    icon: 'code',
    title: 'API統合',
    description: 'APIキーの取得や統合に関する問題',
    action: 'ドキュメントを見る',
    link: '/docs/api/'
  },
  {
    id: 'error',
    icon: 'warning',
    title: 'エラー解決',
    description: 'エラーコードやメッセージの対処法',
    action: 'エラーを検索',
  },
  {
    id: 'setup',
    icon: 'gear',
    title: '初期設定',
    description: 'セットアップや環境構築のサポート',
    action: 'ガイドを開く',
    link: '/docs/setup/'
  },
  {
    id: 'performance',
    icon: 'activity',
    title: 'パフォーマンス',
    description: 'システムの最適化と高速化',
    action: 'チェックリスト',
    link: '/docs/performance/'
  }
];

export function ContactSupportContent({ 
  className = '',
  supportType = 'technical',
  title = '技術サポート',
  subtitle = 'お困りの問題を素早く解決します'
}: ContactSupportContentProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSolution, setSelectedSolution] = useState<string | null>(null);
  const [showContactForm, setShowContactForm] = useState(false);
  
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Search implementation would go here
    // For now, this is a placeholder for search functionality
  };
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  
  return (
    <div className={`${styles.support} ${className}`}>
      {/* Hero Section with Search */}
      <motion.div 
        className={styles.hero}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h1 className={styles.title}>{title}</h1>
        <p className={styles.subtitle}>{subtitle}</p>
        
        <form onSubmit={handleSearch} className={styles.searchForm}>
          <div className={styles.searchWrapper}>
            <Input
              type="search"
              placeholder="エラーメッセージや問題を検索..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              leftIcon="search"
              className={styles.searchInput}
            />
            <Button type="submit" variant="primary">
              検索
            </Button>
          </div>
        </form>
      </motion.div>
      
      {/* Quick Solutions Grid */}
      <motion.div 
        className={styles.quickSolutions}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <h2 className={styles.sectionTitle}>よくあるお問い合わせ</h2>
        <div className={styles.solutionGrid}>
          {quickSolutions.map((solution) => (
            <motion.div
              key={solution.id}
              className={`${styles.solutionCard} ${
                selectedSolution === solution.id ? styles.selected : ''
              }`}
              variants={itemVariants}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
              onClick={() => setSelectedSolution(solution.id)}
            >
              <div className={styles.cardIcon}>
                <Icon name={solution.icon as IconName} />
              </div>
              <h3 className={styles.cardTitle}>{solution.title}</h3>
              <p className={styles.cardDescription}>{solution.description}</p>
              {solution.link ? (
                <a href={solution.link} className={styles.cardAction}>
                  {solution.action}
                  <Icon name="arrow-right" />
                </a>
              ) : (
                <button className={styles.cardAction}>
                  {solution.action}
                  <Icon name="arrow-right" />
                </button>
              )}
            </motion.div>
          ))}
        </div>
      </motion.div>
      
      {/* Emergency Support */}
      <motion.div 
        className={styles.emergencySection}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.6 }}
      >
        <div className={styles.emergencyCard}>
          <div className={styles.emergencyContent}>
            <Icon name="phone" className={styles.emergencyIcon} />
            <div>
              <h3 className={styles.emergencyTitle}>緊急サポート</h3>
              <p className={styles.emergencyText}>
                重大な問題が発生した場合は、直接お電話ください
              </p>
              <a href="tel:0359484959" className={styles.phoneNumber}>
                03-5948-4959
              </a>
              <span className={styles.hours}>平日 9:00-18:00</span>
            </div>
          </div>
        </div>
      </motion.div>
      
      {/* Contact Form Toggle */}
      <motion.div 
        className={styles.contactSection}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6, duration: 0.6 }}
      >
        <AnimatePresence mode="wait">
          {!showContactForm ? (
            <motion.div
              key="button"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className={styles.contactToggle}
            >
              <h2 className={styles.contactTitle}>解決しませんでしたか？</h2>
              <p className={styles.contactText}>
                技術チームが直接サポートいたします
              </p>
              <Button
                variant="primary"
                size="large"
                onClick={() => setShowContactForm(true)}
              >
                お問い合わせフォームを開く
              </Button>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <div className={styles.formHeader}>
                <h2 className={styles.formTitle}>技術サポートへのお問い合わせ</h2>
                <button
                  className={styles.closeForm}
                  onClick={() => setShowContactForm(false)}
                  aria-label="フォームを閉じる"
                >
                  <Icon name="close" />
                </button>
              </div>
              <ContactForm formType={supportType} />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}

export default ContactSupportContent;